import { BehaviorSubject, combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  AppKey,
  AppType,
  FieldMask,
  GetMultiAppRequest,
  ItemsApiService,
  ItemType,
  ListActivatableItemsRequest,
  ListActivatableItemsResponse,
  PartnerApiService,
  SortDirection,
} from '@vendasta/marketplace-apps';
import { Config, SelectedDropdownItem, TemplateState } from './interface';
import { debounceTime, map, scan, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';

const MARKETPLACE_APPS_FALL_BACK_MARKET_ID = 'all-markets-default-fallback';

@Injectable()
export class ItemSearchSelectFilterService {
  _selectedItems: SelectedDropdownItem[] = [];
  _selectionUpdated$$ = new BehaviorSubject(null);
  _triggerLoadMore$$ = new BehaviorSubject(null);
  _pagingInfo$$ = new BehaviorSubject<{ cursor: string; hasMore: boolean }>({ cursor: '', hasMore: true });
  _clearItemList$$ = new Subject<void>();

  get selectedItems(): SelectedDropdownItem[] {
    return this._selectedItems || [];
  }

  constructor(
    private readonly appPartnerService: PartnerApiService,
    private readonly itemsApiService: ItemsApiService,
  ) {}

  init(searchControl$: Observable<string>, config: Config, singleSelect: boolean): Observable<TemplateState> {
    const loadMore$ = this._triggerLoadMore$$.asObservable();
    const searchTerm$ = searchControl$.pipe(
      debounceTime(250),
      startWith(''),
      tap(() => {
        this._pagingInfo$$.next({ cursor: '', hasMore: true });
        this._clearItemList$$.next();
      }),
    );

    const nextActivatableItems$ = combineLatest([loadMore$, searchTerm$]).pipe(
      debounceTime(10),
      withLatestFrom(this._pagingInfo$$.asObservable()),
      switchMap(([[, searchTerm], pagingInfo]) => this.listActivatableItems(config, searchTerm, pagingInfo.cursor)),
      tap((resp) => this._pagingInfo$$.next({ cursor: resp.nextCursor, hasMore: resp.hasMore })),
      map(this.parseListItemsResponse),
    );

    const scanAddFn = this.buildScanFn(config, singleSelect);
    const scanClearFn = () => () => [] as SelectedDropdownItem[];
    const addItemsToList$ = nextActivatableItems$.pipe(map(scanAddFn));
    const clearItemList$ = this._clearItemList$$.asObservable().pipe(map(scanClearFn));

    const itemList$: Observable<SelectedDropdownItem[]> = merge(addItemsToList$, clearItemList$).pipe(
      scan((acc, scanInnerFn) => scanInnerFn(acc), [] as SelectedDropdownItem[]),
    );

    const selectedItems$ = this._selectionUpdated$$
      .asObservable()
      .pipe(switchMap(() => (singleSelect ? this.setItemName(config) : of(this.selectedItems))));

    return combineLatest([selectedItems$, itemList$]).pipe(
      map(([selectedItems, availableItems]) => {
        return { initialized: true, selectedItems, availableItems };
      }),
      startWith({ initialized: false, selectedItems: [], availableItems: [] }),
    );
  }

  updateSelection(selectedItems: SelectedDropdownItem[]): void {
    this._selectedItems = selectedItems;
    this._selectionUpdated$$.next(null);
  }

  buildScanFn(config: Config, singleSelect: boolean) {
    return (value: SelectedDropdownItem[]) => (acc: SelectedDropdownItem[]) => {
      if (acc.length === 0 && singleSelect) {
        acc = config.customDropdownItems ? [...config.customDropdownItems] : [];
      }
      return [...acc, ...value];
    };
  }

  triggerLoadMore(): void {
    this._triggerLoadMore$$.next(null);
  }

  setItemName(config: Config): Observable<SelectedDropdownItem[]> {
    const selectedItem = this.selectedItems?.[0];
    if (!selectedItem || isCustomItem(selectedItem, config)) {
      const wildcardSelectedItem = config.customDropdownItems ? [...config.customDropdownItems] : [];
      this._selectedItems = wildcardSelectedItem;
      return of(wildcardSelectedItem);
    }
    if (selectedItem?.name) {
      return of([selectedItem]);
    }
    return this.hyrdateItemName(selectedItem, config);
  }

  private hyrdateItemName(selectedItem: SelectedDropdownItem, config: Config) {
    const fieldMask = new FieldMask({
      paths: ['AppID', 'Name', 'Type'],
    });
    const appKey = new AppKey({ appId: selectedItem.addonId || selectedItem.appId });
    const marketIdWithFallback = config.marketId ? config.marketId : MARKETPLACE_APPS_FALL_BACK_MARKET_ID;
    // since the List API is paged, any existing selections need to be looked up to display info (eg name)
    const request = new GetMultiAppRequest({
      projectionFilter: fieldMask,
      appKeys: [appKey],
      partnerId: config.partnerId,
      marketId: marketIdWithFallback,
      includeNotEnabled: true,
    });
    return this.appPartnerService.getMultiApp(request).pipe(
      map((resp) => {
        return resp.apps.map((app) => {
          const selectedItem: SelectedDropdownItem = {
            appId: app.key.appId,
            name: app.sharedMarketingInformation.name,
          };
          if (app.appType === AppType.APP_TYPE_ADDON) {
            selectedItem.appId = app.parentRequirements?.parentDetails?.key?.appId;
            selectedItem.addonId = app.key.appId;
          }
          return selectedItem;
        });
      }),
      tap((items) => {
        this._selectedItems = items;
      }),
    );
  }

  parseListItemsResponse(response: ListActivatableItemsResponse): SelectedDropdownItem[] {
    return (response.activatableItemSummaries || []).map((item) => {
      const selectedItem: SelectedDropdownItem = {
        appId: item.itemId,
        name: item.sellerWhitelabelName ? item.sellerWhitelabelName : item.defaultName,
      };
      if (item.itemType === ItemType.ITEM_TYPE_ADDON) {
        selectedItem.addonId = item.itemId;
        selectedItem.appId = item.requiredItems[0].itemId;
      }
      return selectedItem;
    });
  }

  listActivatableItems(config: Config, searchTerm: string, cursor: string): Observable<ListActivatableItemsResponse> {
    const request = new ListActivatableItemsRequest({
      sellerId: config.partnerId,
      storeId: config.marketId,
      cursor: cursor,
      pageSize: 25,
      filters: { ...config.filters, searchTerm: searchTerm },
      sortOptions: { sortDirection: SortDirection.ASCENDING },
    });
    return this.itemsApiService.listActivatableItems(request);
  }
}

function isCustomItem(selectedItem: SelectedDropdownItem, config: Config): boolean {
  if (!config.customDropdownItems) return false;

  return config.customDropdownItems.some((item) => {
    const itemTypes = config.filters?.itemTypes ?? [];
    const isCustomApp = itemTypes.includes(ItemType.ITEM_TYPE_APP) && item.appId === selectedItem.appId;
    const isCustomAddon = itemTypes.includes(ItemType.ITEM_TYPE_ADDON) && item.addonId === selectedItem.addonId;
    return isCustomApp || isCustomAddon;
  });
}
